import { BodySmall, Card, FlexColMd, FlexColSm, FlexMd, FlexSm, Headline5 } from 'components/Elements';
import BoltIcon from 'assets/icons/BoltIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import styled from 'styled-components';
import strings from 'strings/strings.json';
import { Activity, ActivityType, BuildMethod } from 'models/Activity';
import Button from 'components/buttons/Button';
import React from 'react';
import AudioPlayer from 'pages/learner/components/AudioPlayer';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';

type GeneratedContentSectionProps = {
    task: Activity;
};

export default function GeneratedContentSection({ task }: GeneratedContentSectionProps): JSX.Element {
    const { setActiveStep } = React.useContext(BuildCourseContext);

    let status = determineGenerationStatus(task);
    let body = <></>;
    let title = '';

    switch (task.buildMethod) {
        default:
        case BuildMethod.TEXT_AND_IMAGE:
            title = 'Audio';
            break;
        case BuildMethod.AUDIO:
        case BuildMethod.VIDEO:
            title = 'Transcript';
            break;
    }

    switch (status) {
        case 'none':
        default:
            return <></>;
        case 'queued':
        case 'review':
        case 'error':
            body = (
                <Button
                    title={strings.activityUpdatePage.generatedContent.toReview}
                    aria={strings.activityUpdatePage.generatedContent.toReview}
                    onClick={() => setActiveStep('courseAIGeneratedContent')}
                    fullwidth
                />
            );
            break;
        case 'approved':
            switch (task.type) {
                default:
                    return <></>;
                case ActivityType.TASK:
                    switch (task.buildMethod) {
                        case BuildMethod.TEXT_AND_IMAGE:
                            body = <AudioPlayer filepath={task.audioDocument?.s3Filepath ?? ''} />;
                            break;
                        case BuildMethod.AUDIO:
                        case BuildMethod.VIDEO:
                            if (!task.content) return <></>;
                            body = <BodySmall>{task.content}</BodySmall>;
                            break;
                    }
            }
    }

    return (
        <FlexColSm>
            <Card>
                <FlexColMd>
                    <Header>
                        <BoltAndTitle>
                            <BoltIcon size={IconSize.MEDIUM} />
                            <Headline5>{title}</Headline5>
                        </BoltAndTitle>
                    </Header>
                    {body}
                </FlexColMd>
            </Card>
        </FlexColSm>
    );
}

const BoltAndTitle = styled(FlexSm)`
    align-items: center;
`;

const Header = styled(FlexMd)`
    align-items: center;
`;

type ComponentStatus = 'none' | 'queued' | 'error' | 'review' | 'approved';

function determineGenerationStatus(activity: Activity): ComponentStatus {
    let status = activity.generateContentStatus;
    if (!status) {
        return 'none';
    } else if (!status.dateGenerated && !status.dateErrored && !status.dateApproved) {
        return 'queued';
    } else if (status.dateGenerated && !status.dateErrored && !status.dateApproved) {
        return 'review';
    } else if (status.dateErrored) {
        return 'error';
    } else if (
        status.dateGenerated &&
        !status.dateErrored &&
        status.dateApproved &&
        isGeneratedContentAddedToActivity(activity)
    ) {
        return 'approved';
    } else {
        return 'none';
    }
}

function isGeneratedContentAddedToActivity(activity: Activity): boolean {
    switch (activity.type) {
        case ActivityType.TASK:
            switch (activity.buildMethod) {
                case BuildMethod.TEXT_AND_IMAGE:
                    return !!activity.audioDocument?.s3Filepath;
                case BuildMethod.AUDIO:
                case BuildMethod.VIDEO:
                    return !!activity.content;
                default:
                    return false;
            }
        case ActivityType.QUESTIONNAIRE:
            return !!activity.questions?.length;
        default:
            return false;
    }
}
